import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { annotationActions } from 'src/redux/annotation/actions';
import { selectLocalAnnotationCommands } from 'src/redux/annotation/selectors';
import { processAnnotationQueue } from 'src/redux/annotation/thunk/process-annotation-queue';
import { selectAuthUserInfo } from 'src/redux/auth/selectors';
import { AppState } from 'src/redux/types';
import { assertNever } from 'src/utils/assert';
import { useLimitedMode } from 'src/utils/auth/useLimitedMode';
import { SentinelQueryEvaluation, SentinelQueryEvaluator, useSocketList } from 'src/utils/socket';
import { SocketContextSentinel } from 'src/utils/socket/context/sentinelSocketContext';
import { useSocketConnected } from 'src/utils/socket/useSocketConnected';
import { v4 as uuid } from 'uuid';

export interface AnnotationViewModel {
  /** The unique id of the annotation. */
  id: string;
  /** The value of the annotation. */
  comment: string;
  /** The timestamp associated with the annotation. */
  timestamp?: number;
  /** The timestamp when the annotation was created. */
  createdAt: number;
  /** The id of the user who created the annotation. */
  createdBy: string;
  /** The timestamp when the annotation was last updated. */
  updatedAt?: number;
  /** The id of the user who last updated the annotation. */
  updatedBy?: string;
  /** The timestamp when the annotation was deleted. */
  deletedOn?: string;
  /** Whether or not any operations on this annotation are pending sync. */
  isPendingSync: boolean;
}

export interface AnnotationsContextData {
  /** The list of annotations associated with the context. */
  annotations: AnnotationViewModel[];
  /**
   * Creates a new annotation for the entity associated in this context.
   * @param comment The value of the annotation.
   * @param timestamp The point in time at which to associate this annotation.
   */
  create: (comment: string, timestamp?: number) => void;
  /**
   * Updates an existing annotation.
   * @param id The id of the annotation to update.
   * @param comment The updated value of the annotation.
   */
  update: (id: string, comment: string) => void;
  /**
   * Deletes an existing annotation.
   * @param id The id of the annotation to delete.
   */
  remove: (id: string) => void;
  /**
   * Restores a removed annotation.
   * @param id The id of the annotation to restore.
   */
  restore: (id: string) => void;
}

/**
 * A context which provides data about annotations associated with a specific entity, as well as functions for performing CRUD operations on that data.
 * Do not consume directly unless you are implementing a custom context provider.
 */
export const AnnotationsContext = React.createContext<AnnotationsContextData>(undefined as any);

const annotationQueryEvaluator: SentinelQueryEvaluator<'annotations'> = (data, query) => {
  if (!query?.query?.parentId) return SentinelQueryEvaluation.Indeterminate; // If unset, we have no idea, so err on the side of fetching fresh data.
  return query.query.parentId === data.parentId ? SentinelQueryEvaluation.Include : SentinelQueryEvaluation.Exclude;
};

interface AnnotationsContextProviderProps {
  /** The id of the entity that for which all annotations within the context will be associated with. */
  parentId?: string;
  /** A local identifier used to associate annotations within the context to a parentId, when the parentId is not yet available. */
  parentClaimKey?: string;
}

/** The default implementation of the annotation context provider. */
export const AnnotationsContextProvider: React.FC<AnnotationsContextProviderProps> = ({
  parentId,
  parentClaimKey,
  children,
}) => {
  const dispatch = useDispatch<ThunkDispatch<AppState, undefined, AnyAction>>();
  const { isLimited } = useLimitedMode();

  const query = React.useMemo(() => {
    if (!parentId) return null;
    // NOTE - The shape of this query is being relied on in the sentinelReducer. If this is changed here,
    // the annotationActions.annotationSyncCommandSuccess handler must also be changed.
    return { query: { parentId } };
  }, [parentId]);
  const { items: remoteAnnotations } = useSocketList(
    SocketContextSentinel,
    'annotations',
    query,
    undefined,
    annotationQueryEvaluator,
  );
  const localAnnotationCommands = useSelector(selectLocalAnnotationCommands);
  const user = useSelector(selectAuthUserInfo);
  const socketConnected = useSocketConnected();

  // An effect that binds annotations within this context to their parentId, when the parentId becomes available.
  React.useEffect(() => {
    if (parentClaimKey && parentId) {
      dispatch(annotationActions.annotationClaimCommand({ parentClaimKey, parentId }));
    }
  }, [dispatch, parentClaimKey, parentId]);

  // An effect that triggers processing the annotation command queue any time there is something to process.
  React.useEffect(() => {
    if (!isLimited && localAnnotationCommands.length && socketConnected) {
      dispatch(processAnnotationQueue());
    }
  }, [dispatch, isLimited, localAnnotationCommands.length, socketConnected]);

  const create: AnnotationsContextData['create'] = React.useCallback(
    (comment, timestamp) => {
      const action = annotationActions.annotationQueueCommand({
        command: { type: 'create', timestamp, comment, parentId, annotationId: uuid(), parentClaimKey },
      });
      dispatch(action);
    },
    [dispatch, parentClaimKey, parentId],
  );

  const update: AnnotationsContextData['update'] = React.useCallback(
    (id, comment) => {
      const action = annotationActions.annotationQueueCommand({
        command: { type: 'update', comment, annotationId: id },
      });
      dispatch(action);
    },
    [dispatch],
  );

  const remove: AnnotationsContextData['remove'] = React.useCallback(
    id => {
      const action = annotationActions.annotationQueueCommand({ command: { type: 'remove', annotationId: id } });
      dispatch(action);
    },
    [dispatch],
  );

  const restore: AnnotationsContextData['restore'] = React.useCallback(
    id => {
      const action = annotationActions.annotationQueueCommand({ command: { type: 'restore', annotationId: id } });
      dispatch(action);
    },
    [dispatch],
  );

  const annotations = React.useMemo<AnnotationViewModel[]>(() => {
    // Map the remote annotations to the view model.
    const result = remoteAnnotations.map(i => {
      const r: AnnotationViewModel = {
        id: i._id,
        comment: i.comment,
        timestamp: i.timestamp ?? undefined,
        createdAt: i.createdAt,
        createdBy: i.createdBy,
        updatedAt: i.updatedAt ?? undefined,
        updatedBy: i.updatedBy ?? undefined,
        deletedOn: i.deletedOn ?? undefined,
        isPendingSync: false,
      };
      return r;
    });

    // Apply the relevant local commands to the view models.
    for (const cmd of localAnnotationCommands) {
      switch (cmd.type) {
        case 'create': {
          const idMatches = !!cmd.parentId && cmd.parentId === parentId;
          const claimKeyMatches = !!cmd.parentClaimKey && cmd.parentClaimKey === parentClaimKey;
          // If the command type is create, and it's relevant to this context, insert it into the list.
          if (!idMatches && !claimKeyMatches) continue;
          const existing = result.find(i => i.id === cmd.annotationId);
          if (existing) continue; // If match exists, don't insert another
          const newAnnotation: AnnotationViewModel = {
            id: cmd.annotationId,
            comment: cmd.comment,
            timestamp: cmd.timestamp,
            createdAt: cmd.issuedAt,
            createdBy: user?.id || 'ERROR',
            isPendingSync: true,
          };
          result.push(newAnnotation);
          break;
        }
        case 'update': {
          const existing = result.find(i => i.id === cmd.annotationId);
          if (!existing) continue;
          existing.comment = cmd.comment;
          existing.updatedAt = cmd.issuedAt;
          existing.updatedBy = user?.id;
          existing.isPendingSync = true;
          break;
        }
        case 'remove': {
          const existing = result.find(i => i.id === cmd.annotationId);
          if (!existing) continue;
          existing.deletedOn = new Date(cmd.issuedAt).toISOString();
          existing.updatedAt = cmd.issuedAt;
          existing.updatedBy = user?.id;
          existing.isPendingSync = true;
          break;
        }
        case 'restore': {
          const existing = result.find(i => i.id === cmd.annotationId);
          if (!existing) continue;
          existing.deletedOn = undefined;
          existing.updatedAt = cmd.issuedAt;
          existing.updatedBy = user?.id;
          existing.isPendingSync = true;
          break;
        }
        default:
          assertNever(cmd);
      }
    }

    // Sort the result (desc by created at).
    result.sort((a, b) => {
      if (a.createdAt < b.createdAt) return 1;
      if (a.createdAt > b.createdAt) return -1;
      return 0;
    });
    return result;
  }, [localAnnotationCommands, parentClaimKey, parentId, remoteAnnotations, user?.id]);

  const contextValue = React.useMemo<AnnotationsContextData>(() => {
    const v: AnnotationsContextData = { annotations, create, update, remove, restore };
    return v;
  }, [annotations, create, remove, restore, update]);

  return <AnnotationsContext.Provider value={contextValue}>{children}</AnnotationsContext.Provider>;
};

export const useAnnotationsContext = (): AnnotationsContextData => {
  return React.useContext(AnnotationsContext);
};
